import { Component, createRef } from 'preact';
import { PureComponent } from 'preact/compat';
import IconButton from 'preact-material-components/IconButton';
import 'preact-material-components/IconButton/style.css';

const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const SPACEBAR = 32;

const isStyleObject = (obj: Object | boolean) => typeof obj === 'object';

type OtpInputProps = {
  className: string,
  containerStyle: Object,
  disabledStyle: Object,
  errorStyle: Object,
  focusStyle: Object,
  hasErrored: boolean,
  inputStyle: Object,
  isDisabled: boolean,
  isInputNum: boolean,
  isInputSecure: boolean,
  numInputs: number,
  onFocus: any,
  onPaste: any,
  onBeforeInput: any,
  onChange: any,
  onKeyDown: any,
  onInput: any,
  onBlur: any,
  isLastChild: boolean,
  index: number,
  focus: any,
  placeholder: string,
  separator: Object,
  shouldAutoFocus: boolean,
  value: string,
  withInfoIcon?: boolean,
};

type OtpInputElementProps = {
  className: string,
  containerStyle: Object,
  disabledStyle: Object,
  errorStyle: Object,
  focusStyle: Object,
  hasErrored: boolean,
  inputStyle: Object,
  isDisabled: boolean,
  isInputNum: boolean,
  isInputSecure: boolean,
  numInputs: number,
  onChange: Function,
  placeholder: string,
  separator: Object,
  shouldAutoFocus: boolean,
  value: string | number,
};

type OtpInputState = {
  activeInput: number,
  otp?: string[],
};

class SingleOtpInput extends PureComponent<OtpInputProps> {
  input = createRef();

  componentDidMount() {
    const { focus, shouldAutoFocus } = this.props;
    const { current: inputEl } = this.input;

    if (inputEl && focus && shouldAutoFocus) {
      inputEl.focus();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot: OtpInputProps) {
    const { focus } = this.props;
    const { current: inputEl } = this.input;

    if (prevProps.focus !== focus && inputEl && focus) {
      inputEl.focus();
      inputEl.select();
    }
  }

  getInputType = () => {
    const { isInputSecure, isInputNum } = this.props;

    if (isInputSecure) {
      return 'password';
    }

    if (isInputNum) {
      return 'tel';
    }

    return 'text';
  };

  render(props, state, context) {
    const {
      placeholder,
      separator,
      isLastChild,
      inputStyle,
      focus,
      isDisabled,
      hasErrored,
      errorStyle,
      focusStyle,
      disabledStyle,
      shouldAutoFocus,
      isInputNum,
      index,
      value,
      className,
      isInputSecure,
      ...rest
    } = this.props;

    return (
      <div
        className={className}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <input
          aria-label={`${
            index === 0 ? 'Please enter verification code. ' : ''
          }${isInputNum ? 'Digit' : 'Character'} ${index + 1}`}
          autoComplete='off'
          style={Object.assign(
            isStyleObject(inputStyle) && inputStyle,
            focus && isStyleObject(focusStyle) && focusStyle,
            isDisabled && isStyleObject(disabledStyle) && disabledStyle,
            hasErrored && isStyleObject(errorStyle) && errorStyle
          )}
          placeholder={placeholder}
          type={this.getInputType()}
          maxLength={1}
          ref={this.input}
          disabled={isDisabled}
          value={value ? value : ''}
          {...rest}
        />
        {!isLastChild && separator}
      </div>
    );
  }
}

class SplitInput extends Component<OtpInputElementProps, OtpInputState> {
  defaultProps = {
    numInputs: 4,
    isDisabled: false,
    shouldAutoFocus: false,
    isInputSecure: false,
  };

  state = {
    activeInput: 0,
  };

  getOtpValue = () =>
    this.props.value ? this.props.value.toString().split('') : [];

  getPlaceholderValue = () => {
    const { placeholder, numInputs } = this.props;

    if (typeof placeholder === 'string') {
      if (placeholder.length === numInputs) {
        return placeholder;
      }

      if (placeholder.length > 0) {
        console.error(
          'Length of the placeholder should be equal to the number of inputs.'
        );
      }
    }
  };

  handleOtpChange = (otp: any[]) => {
    const { onChange } = this.props;
    const otpValue = otp.join('');

    onChange(otpValue);
  };

  isInputValueValid = (value: string) => {
    const isTypeValid = this.props.isInputNum
      ? !isNaN(parseInt(value, 10))
      : typeof value === 'string';

    return isTypeValid && value.trim().length === 1;
  };

  focusInput = (input: number) => {
    const { numInputs } = this.props;
    const activeInput = Math.max(Math.min(numInputs - 1, input), 0);

    this.setState({ activeInput });
  };

  focusNextInput = () => {
    const { activeInput } = this.state;
    this.focusInput(activeInput + 1);
  };

  focusPrevInput = () => {
    const { activeInput } = this.state;
    this.focusInput(activeInput - 1);
  };

  changeCodeAtFocus = (value: string | string[]) => {
    const { activeInput } = this.state;
    const otp = this.getOtpValue();
    otp[activeInput] = value[0];

    this.handleOtpChange(otp);
  };

  handleOnPaste = (e: any) => {
    e.preventDefault();

    const { activeInput: initialActiveInput } = this.state;
    let activeInput = initialActiveInput;
    const { numInputs, isDisabled } = this.props;

    if (isDisabled) {
      return;
    }

    const otp = this.getOtpValue();
    if (initialActiveInput > otp.length) {
      activeInput = otp.length;
    }
    let nextActiveInput = activeInput;

    const pastedData =
      e.type === 'beforeinput'
        ? e.data.slice(0, numInputs - activeInput).split('')
        : e.clipboardData
            .getData('text/plain')
            .slice(0, numInputs - activeInput)
            .split('');

    if (this.props.isInputNum) {
      if (
        pastedData.find((item: any) => {
          return isNaN(parseInt(item, 10));
        })
      ) {
        return;
      }
    }

    for (let pos = 0; pos < numInputs; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        otp[pos] = pastedData.shift();
        nextActiveInput++;
      }
    }

    this.setState({ activeInput: nextActiveInput }, () => {
      this.focusInput(nextActiveInput);
      this.handleOtpChange(otp);
    });
  };

  handleOnKeyDown = (e: any) => {
    if (e.keyCode === BACKSPACE || e.key === 'Backspace') {
      e.preventDefault();
      this.changeCodeAtFocus('');
      this.focusPrevInput();
    } else if (e.keyCode === DELETE || e.key === 'Delete') {
      e.preventDefault();
      this.changeCodeAtFocus('');
    } else if (e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft') {
      e.preventDefault();
      this.focusPrevInput();
    } else if (e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight') {
      e.preventDefault();
      this.focusNextInput();
    } else if (
      e.keyCode === SPACEBAR ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.key === 'Space'
    ) {
      e.preventDefault();
    }
  };

  handleOnInput = (e: any) => {
    if (this.isInputValueValid(e.target.value)) {
      this.changeCodeAtFocus(e.target.value);
      const otp = this.getOtpValue();
      if (this.state.activeInput > otp.length) {
        this.focusInput(otp.length + 1);
      } else {
        this.focusNextInput();
      }
    } else {
      if (this.props.isInputNum) {
        e.preventDefault();
        this.changeCodeAtFocus('');
        this.focusInput(this.state.activeInput);
      } else {
        const { nativeEvent } = e;

        if (
          nativeEvent.data === null &&
          nativeEvent.inputType === 'deleteContentBackward'
        ) {
          e.preventDefault();
          this.changeCodeAtFocus('');
          this.focusPrevInput();
        }
      }
    }
  };

  renderInputs = () => {
    const { activeInput } = this.state;
    const {
      numInputs,
      inputStyle,
      focusStyle,
      separator,
      isDisabled,
      disabledStyle,
      hasErrored,
      errorStyle,
      shouldAutoFocus,
      isInputNum,
      isInputSecure,
      className,
    } = this.props;

    const inputs = [];
    const otp = this.getOtpValue();
    const placeholder = this.getPlaceholderValue();

    for (let i = 0; i < numInputs; i++) {
      inputs.push(
        <SingleOtpInput
          {...this.defaultProps}
          placeholder={placeholder && placeholder[i]}
          key={i}
          index={i}
          focus={activeInput === i}
          value={otp && otp[i]}
          onChange={this.handleOnInput}
          onKeyDown={this.handleOnKeyDown}
          onPaste={this.handleOnPaste}
          onBeforeInput={(e: any) => {
            if (e.data && e.data.length > 1) {
              this.handleOnPaste(e);
            }
          }}
          onFocus={(e: any) => {
            this.setState({ activeInput: i });
            e.target.select();
          }}
          onBlur={() => this.setState({ activeInput: -1 })}
          separator={separator}
          inputStyle={inputStyle}
          focusStyle={focusStyle}
          isLastChild={i === numInputs - 1}
          isDisabled={isDisabled}
          disabledStyle={disabledStyle}
          hasErrored={hasErrored}
          errorStyle={errorStyle}
          shouldAutoFocus={shouldAutoFocus}
          isInputNum={isInputNum}
          isInputSecure={isInputSecure}
          className={className}
        />
      );
    }

    return inputs;
  };

  render(props, state, context) {
    const { containerStyle, withInfoIcon, onInfoIconClick } = this.props;

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          style={Object.assign(
            { display: 'flex' },
            isStyleObject(containerStyle) && containerStyle
          )}
          className={!isStyleObject(containerStyle) ? containerStyle : ''}
        >
          {this.renderInputs()}
        </div>
        {withInfoIcon && (
          <IconButton
            style={{
              color: '#656565',
              cursor: 'pointer',
            }}
            onClick={onInfoIconClick}
          >
            <IconButton.Icon on>info_outline</IconButton.Icon>
            <IconButton.Icon >info_outline</IconButton.Icon>
          </IconButton>
        )}
      </div>
    );
  }
}

export default SplitInput;



// WEBPACK FOOTER //
// ./components/checkoutWidget/SplitInput.js